import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import { useCreateTeam, useCoachTeam } from '@/hooks/use-coach';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users,
  ArrowRight,
  Check,
  Copy,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics/posthog';

type Step = 'name' | 'success';

export default function CreateTeamPage() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data: existingTeams } = useCoachTeam();
  const createTeamMutation = useCreateTeam();

  const [step, setStep] = useState<Step>('name');
  const [teamName, setTeamName] = useState('');
  const [createdTeam, setCreatedTeam] = useState<{ id: number; name: string; inviteCode: string } | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation('/login');
      return;
    }

    if (!isLoading && user?.accountType !== 'coach') {
      setLocation('/');
      return;
    }

    // If coach already has a team, redirect to dashboard
    if (existingTeams && existingTeams.length > 0) {
      setLocation('/coach/dashboard');
    }
  }, [isAuthenticated, isLoading, user, existingTeams, setLocation]);

  if (isLoading) {
    return (
      <div className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.accountType !== 'coach') {
    return null;
  }

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast({
        title: 'Team name required',
        description: 'Please enter a name for your team.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const team = await createTeamMutation.mutateAsync(teamName.trim());
      setCreatedTeam({
        id: team.id,
        name: team.name,
        inviteCode: team.inviteCode,
      });
      setStep('success');
      trackEvent('team_created', {
        team_name: team.name,
        team_id: team.id,
      });
    } catch (error) {
      // Error is handled by the mutation's onError
    }
  };

  const copyInviteCode = () => {
    if (createdTeam?.inviteCode) {
      navigator.clipboard.writeText(createdTeam.inviteCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
      toast({
        title: 'Copied!',
        description: 'Invite code copied to clipboard.',
      });
    }
  };

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <div className="container mx-auto px-6">
        <div className="max-w-lg mx-auto">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center gap-2">
              <StepIndicator
                step={1}
                label="Name"
                isActive={step === 'name'}
                isComplete={step === 'success'}
              />
              <div className="w-8 h-0.5 bg-gray-300 dark:bg-gray-600" />
              <StepIndicator
                step={2}
                label="Done"
                isActive={step === 'success'}
                isComplete={false}
              />
            </div>
          </div>

          {/* Step 1: Team Name */}
          {step === 'name' && (
            <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl">Create Your Team</CardTitle>
                <CardDescription>
                  Give your team a name that your athletes will recognize.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="teamName">Team Name</Label>
                  <Input
                    id="teamName"
                    placeholder="e.g., Elite Basketball Academy"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    className="text-lg py-6"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && teamName.trim()) {
                        handleCreateTeam();
                      }
                    }}
                  />
                </div>

                <Button
                  className="w-full btn-primary py-6 text-lg rounded-2xl"
                  onClick={handleCreateTeam}
                  disabled={!teamName.trim() || createTeamMutation.isPending}
                >
                  {createTeamMutation.isPending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      Create Team
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Success */}
          {step === 'success' && createdTeam && (
            <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl">Team Created!</CardTitle>
                <CardDescription>
                  Your team "{createdTeam.name}" is ready. Share the invite code with your athletes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Invite Code */}
                <div className="glass-card dark:glass-card-dark rounded-3xl p-6 text-center">
                  <p className="text-sm text-gray-500 mb-2">Invite Code</p>
                  <div className="flex items-center justify-center gap-4">
                    <span className="font-mono text-3xl tracking-wider font-bold">
                      {createdTeam.inviteCode}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyInviteCode}
                      className="shrink-0"
                    >
                      {copiedCode ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="glass-card dark:glass-card-dark rounded-2xl p-4 border-l-4 border-[var(--apple-blue)]">
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    <strong>How it works:</strong> Your athletes enter this code when they join your team.
                    They'll use your team credits for generating training plans.
                  </p>
                </div>

                <Button className="w-full btn-primary py-6 text-lg rounded-2xl" onClick={() => setLocation('/coach/dashboard')}>
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
}

function StepIndicator({
  step,
  label,
  isActive,
  isComplete,
}: {
  step: number;
  label: string;
  isActive: boolean;
  isComplete: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
          isComplete
            ? 'bg-[var(--apple-green)] text-white shadow-lg'
            : isActive
            ? 'bg-[var(--apple-blue)] text-white shadow-lg'
            : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
        }`}
      >
        {isComplete ? <Check className="w-4 h-4" /> : step}
      </div>
      <span
        className={`text-xs ${
          isActive || isComplete ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400'
        }`}
      >
        {label}
      </span>
    </div>
  );
}
